/**
 * useArTableSplit
 *
 * Resolves the form's active split filter to the column filter one ArTable must
 * apply, or `null` when that table has no column matching the split criterion.
 * Consumers: `ArTableControls` (via the `split` prop it threads into the row
 * query) and anything else mounting a table inside a split form.
 *
 * Inside the PDF export portal the split comes from `PdfExportContext`, so a
 * per-manufacturer document renders against its own split while the on-screen
 * `formStore.state.splitQuery` is left untouched (SYD-2302). Off-portal the
 * override is absent and the live store value wins.
 *
 * Subscribes to exactly one leaf, `formStore.state.splitQuery`. `useFormStore()`
 * is banned here: it spreads `formVersion.data`, so every cell keystroke would
 * re-run this hook and rebuild the table's row query (SYD-2252). The leaf is
 * read into a variable *before* the `??`: `??` short-circuits, so with an
 * override present the snapshot result would go unread — and a `useSnapshot`
 * whose result is never read subscribes to the whole proxy, re-rendering every
 * in-portal node on any form write.
 *
 * The snapshot cast is the deep-readonly unwrap: `Description.contextRules.rules`
 * is an array, which Valtio's `Snapshot<T>` makes `readonly` and therefore not
 * assignable to `SplitQuery`. Nothing downstream mutates it.
 */

import { useMemo } from 'react';
import { useSnapshot } from 'valtio';
import type { ArColumnDefinition } from '#types/System.Artifacts.types';
import type { SplitQuery } from '#types/System.types';
import { formStore } from '../../../store';
import { usePdfExportContext } from '../../pdfExport/context/PdfExportContext';
import type { ArTableSplit } from './ArTableSplit';
import { resolveSplitForTable } from './resolveSplitForTable';

function useArTableSplit(columns: readonly ArColumnDefinition[]): ArTableSplit | null {
  const { splitQuery: exportSplitQuery } = usePdfExportContext();
  const liveSplitQuery = useSnapshot(formStore.state).splitQuery as SplitQuery | undefined;
  const splitQuery = exportSplitQuery ?? liveSplitQuery;

  return useMemo(
    () => resolveSplitForTable({ columns, splitQuery }),
    [columns, splitQuery],
  );
}

export { useArTableSplit };
